import { IndexUiState, UiState } from 'instantsearch.js/es/types';
import { normalizeRefinements } from '../algolia-helpers';
import { BaseRouteState, RoutingConfig } from './types';

export function stateMapping<U extends string>({ enabledRefinementLists, index, defaultRouteState }: RoutingConfig<U>) {
    return {
        /**
         * Converts Algolia UI state to route state
         * @param uiState - The current UI state from Algolia
         * @returns RouteState object with extracted values
         */
        stateToRoute(uiState: UiState): BaseRouteState {
            const indexUiState = uiState[index];

            if (!indexUiState) {
                return { ...defaultRouteState };
            }

            const routeState: BaseRouteState = {
                ...defaultRouteState,
                ...extractRefinementLists(indexUiState.refinementList, enabledRefinementLists),
            };

            if (indexUiState.page) {
                routeState.page = indexUiState.page;
            }

            return routeState;
        },

        /**
         * Converts route state to Algolia UI state
         * @param routeState - The current route state
         * @returns UiState object for Algolia
         */
        routeToState(routeState: BaseRouteState): UiState {
            const indexState = {
                ...(buildRefinementListState(routeState, enabledRefinementLists) as {
                    refinementList: Record<string, string[]>;
                }),
                ...(routeState.page !== null && { page: routeState.page }),
            };

            return {
                [index]: indexState,
            };
        },
    };
}

/**
 * Extracts refinement list values from UI state
 * @param refinementList - The refinement list from UI state
 * @returns Object with extracted refinement values
 */
function extractRefinementLists(
    refinementList: IndexUiState['refinementList'],
    enabledRefinementLists: string[]
): Omit<BaseRouteState, 'page'> {
    const result = {} as Record<string, string[]>;

    for (const listName of enabledRefinementLists) {
        result[listName] = refinementList?.[listName] ?? [];
    }

    return result as BaseRouteState;
}

/**
 * Builds refinement list state for UI state
 * @param routeState - The current route state
 * @returns Object with refinement list configuration
 */
function buildRefinementListState(routeState: BaseRouteState, enabledRefinementLists: string[]) {
    const refinementList = {} as Record<string, string[]>;

    for (const listName of enabledRefinementLists) {
        refinementList[listName] = routeState[listName] as string[];
    }

    return { refinementList: normalizeRefinements(refinementList) };
}

export default stateMapping;
